// src/components/UserProfile/UserProfile.tsx
// Replaces <Avatar aria-label="Guest" /> with a real user avatar + dropdown

import React, { useState, useEffect, useRef } from "react";

interface User {
  firstName?: string;
  lastName?: string;
  email?: string;
  role?: string;
}

interface UserProfileProps {
  user: User | null;
  onLogout: () => void;
}

function getInitials(user: User | null): string {
  if (!user) return "?";
  const f = user.firstName?.[0] ?? "";
  const l = user.lastName?.[0] ?? "";
  return (f + l).toUpperCase() || user.email?.[0]?.toUpperCase() || "?";
}

// Pick a consistent colour from the user's name
function getAvatarColor(user: User | null): string {
  const colors = [
    "#1473e6", "#0E7490", "#065F46", "#5B21B6",
    "#9D174D", "#92400E", "#1E3A5F", "#78350F",
  ];
  const str = (user?.firstName ?? "") + (user?.lastName ?? "") + (user?.email ?? "");
  let hash = 0;
  for (let i = 0; i < str.length; i++) hash = str.charCodeAt(i) + ((hash << 5) - hash);
  return colors[Math.abs(hash) % colors.length];
}

const UserProfile: React.FC<UserProfileProps> = ({ user, onLogout }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === "Escape") setOpen(false); };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, []);

  const initials   = getInitials(user);
  const avatarColor = getAvatarColor(user);
  const fullName   = user ? `${user.firstName ?? ""} ${user.lastName ?? ""}`.trim() : "Guest";
  const email      = user?.email ?? "";

  return (
    <>
      <style>{`
        .cpn-profile-trigger {
          width: 32px;
          height: 32px;
          border-radius: 50%;
          border: 2px solid transparent;
          background: ${avatarColor};
          color: #fff;
          font-size: 12px;
          font-weight: 700;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          font-family: 'Segoe UI', sans-serif;
          transition: border-color 0.15s ease, box-shadow 0.15s ease;
          flex-shrink: 0;
          letter-spacing: 0.3px;
        }
        .cpn-profile-trigger:hover {
          border-color: rgba(255,255,255,0.5);
          box-shadow: 0 0 0 2px ${avatarColor}44;
        }
        .cpn-profile-trigger.open {
          border-color: #fff;
          box-shadow: 0 0 0 2px ${avatarColor};
        }

        .cpn-profile-popup {
          position: fixed;
          top: 52px;
          right: 8px;
          z-index: 9999;
          width: 280px;
          background: #fff;
          border-radius: 14px;
          box-shadow: 0 8px 40px rgba(0,0,0,0.16), 0 2px 8px rgba(0,0,0,0.06);
          overflow: hidden;
          animation: profileIn 0.18s cubic-bezier(0.16,1,0.3,1) forwards;
          transform-origin: top right;
        }

        @keyframes profileIn {
          from { opacity: 0; transform: scale(0.94) translateY(-6px); }
          to   { opacity: 1; transform: scale(1)    translateY(0); }
        }

        /* User identity block */
        .cpn-profile-identity {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 20px 16px 16px;
          border-bottom: 1px solid #f0f0f0;
        }
        .cpn-profile-avatar-lg {
          width: 48px;
          height: 48px;
          border-radius: 50%;
          background: ${avatarColor};
          color: #fff;
          font-size: 18px;
          font-weight: 700;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
          font-family: 'Segoe UI', sans-serif;
          letter-spacing: 0.3px;
        }
        .cpn-profile-name {
          font-size: 14px;
          font-weight: 700;
          color: #1a1a1a;
          font-family: 'Segoe UI', sans-serif;
          margin-bottom: 2px;
        }
        .cpn-profile-email {
          font-size: 11px;
          color: #8e8e8e;
          font-family: 'Segoe UI', sans-serif;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          max-width: 180px;
        }
        .cpn-profile-role {
          display: inline-block;
          margin-top: 4px;
          font-size: 10px;
          font-weight: 600;
          color: ${avatarColor};
          background: ${avatarColor}18;
          padding: 2px 7px;
          border-radius: 10px;
          font-family: 'Segoe UI', sans-serif;
          text-transform: capitalize;
        }

        /* Menu items */
        .cpn-profile-menu {
          padding: 6px 0;
        }
        .cpn-profile-menu-item {
          display: flex;
          align-items: center;
          gap: 10px;
          width: 100%;
          padding: 10px 16px;
          border: none;
          background: transparent;
          text-align: left;
          font-size: 13px;
          font-weight: 500;
          color: #2c2c2c;
          cursor: pointer;
          font-family: 'Segoe UI', sans-serif;
          transition: background 0.1s ease;
        }
        .cpn-profile-menu-item:hover { background: #f5f5f5; }
        .cpn-profile-menu-item.danger { color: #d7373f; }
        .cpn-profile-menu-item.danger:hover { background: #fff5f5; }
        .cpn-profile-menu-icon {
          font-size: 16px;
          width: 20px;
          text-align: center;
          flex-shrink: 0;
        }
        .cpn-profile-divider {
          height: 1px;
          background: #f0f0f0;
          margin: 4px 0;
        }

        /* Footer */
        .cpn-profile-footer {
          padding: 10px 16px;
          border-top: 1px solid #f0f0f0;
          background: #fafafa;
          font-size: 10px;
          color: #b0b0b0;
          font-family: 'Segoe UI', sans-serif;
          text-align: center;
        }
      `}</style>

      <div ref={ref} style={{ position: "relative", display: "inline-flex" }}>
        {/* Trigger — avatar circle with initials */}
        <button
          className={`cpn-profile-trigger${open ? " open" : ""}`}
          onClick={() => setOpen(o => !o)}
          aria-label="User profile"
          aria-expanded={open}
          title={fullName}
        >
          {initials}
        </button>

        {/* Dropdown */}
        {open && (
          <div className="cpn-profile-popup">

            {/* Identity */}
            <div className="cpn-profile-identity">
              <div className="cpn-profile-avatar-lg">{initials}</div>
              <div style={{ minWidth: 0 }}>
                <div className="cpn-profile-name">{fullName || "User"}</div>
                <div className="cpn-profile-email">{email}</div>
                {user?.role && (
                  <span className="cpn-profile-role">{user.role}</span>
                )}
              </div>
            </div>

            {/* Menu */}
            <div className="cpn-profile-menu">
              <button className="cpn-profile-menu-item" onClick={() => setOpen(false)}>
                <span className="cpn-profile-menu-icon">👤</span>
                My Profile
              </button>
              <button className="cpn-profile-menu-item" onClick={() => setOpen(false)}>
                <span className="cpn-profile-menu-icon">⚙️</span>
                Account Settings
              </button>
              <button className="cpn-profile-menu-item" onClick={() => setOpen(false)}>
                <span className="cpn-profile-menu-icon">💳</span>
                Billing & Plans
              </button>
              <button className="cpn-profile-menu-item" onClick={() => setOpen(false)}>
                <span className="cpn-profile-menu-icon">🔔</span>
                Notifications
              </button>

              <div className="cpn-profile-divider" />

              <button className="cpn-profile-menu-item" onClick={() => setOpen(false)}>
                <span className="cpn-profile-menu-icon">❓</span>
                Help & Support
              </button>
              <button className="cpn-profile-menu-item" onClick={() => setOpen(false)}>
                <span className="cpn-profile-menu-icon">📋</span>
                Privacy Policy
              </button>

              <div className="cpn-profile-divider" />

              <button
                className="cpn-profile-menu-item danger"
                onClick={() => { setOpen(false); onLogout(); }}
              >
                <span className="cpn-profile-menu-icon">🚪</span>
                Sign out
              </button>
            </div>

            <div className="cpn-profile-footer">
              Cilpron Ltd · v1.0 · {new Date().getFullYear()}
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default UserProfile;